// The area card's price-development chip: a small "↗ +34 %" in the card's heading (no sparkline, so the
// card keeps its height). The card is a plain HTML string (getTooltipData), so the chip is filled in after
// the card is in the DOM: one install-once MutationObserver fills every card wherever it appears (kartvy
// hover and sticky cards, listvy's info panel and phone card). The series comes from the backend's
// /price-history/area/:regso (price_history.py), cached per area+type for the session and fetched only after
// a short settle, so moving the mouse over many areas doesn't flood the endpoint. The chip stays hidden
// until there is data, so areas without enough sales keep the card exactly as it was.
//
// `fetchAreaPriceChange` exposes the same cached series to React, for the listing card's "+X % i området".
import { apiFetch } from './edgePayloads.js';

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'https://api.xn--omrdeskollen-vcb.nu';
const ENDPOINT = `${API_BASE}/price-history/area`;
const HOVER_DELAY_MS = 220;
const TYPE = 'apartment'; // the card's headline trend; apartments are the densest data

// The trend shown by default is the last 3 years, not the whole (~10 year) curve: the recent window is more
// relevant to someone buying now. Shared by the area-card chip, the listing-card pill and the popup chart.
export const DEFAULT_TREND_YEARS = 3;

// The % change over the last `years` years of a quarterly series ([{ sqmPrice, year }], oldest first), or the
// whole series when `years` is null or the series is shorter. Returns { change, startYear, full } or null.
export const priceChangeOverYears = (series, years = DEFAULT_TREND_YEARS) => {
    if (!Array.isArray(series) || series.length < 2) return null;
    const latest = series[series.length - 1].sqmPrice;
    if (!latest) return null;
    const back = years == null ? series.length : years * 4;
    const startIdx = Math.max(0, (series.length - 1) - back);
    const start = series[startIdx];
    if (!start || !start.sqmPrice) return null;
    return { change: Math.round((latest / start.sqmPrice - 1) * 100), startYear: start.year, full: startIdx === 0 };
};

const cache = new Map(); // "<regso>|<type>" -> Promise<area series | null>

export const fetchAreaSeries = (regso, type = TYPE) => {
    const key = `${regso}|${type}`;
    if (cache.has(key)) return cache.get(key);
    const promise = apiFetch(`${ENDPOINT}/${encodeURIComponent(regso)}?type=${encodeURIComponent(type)}`)
        .then((response) => (response.ok ? response.json() : null))
        .then((data) => (data && Array.isArray(data.series) && data.series.length >= 4 ? data : null))
        .catch(() => null);
    cache.set(key, promise);
    return promise;
};

// The default-window (3 year) change for React callers (the listing card), or null when there isn't enough.
export const fetchAreaPriceChange = (regso, type = TYPE, years = DEFAULT_TREND_YEARS) => fetchAreaSeries(regso, type).then((data) => {
    if (!data || !Array.isArray(data.series)) return null;
    const win = priceChangeOverYears(data.series, years);
    return win ? { ...win, years } : null;
});

// The tooltip for a windowed trend: "senaste 3 åren", or "sedan <year>" when the series is shorter.
const trendTitle = (win, years, swedish) => {
    if (win.full) return swedish ? `Prisutveckling i området sedan ${win.startYear}` : `Area price trend since ${win.startYear}`;
    return swedish ? `Prisutveckling i området, senaste ${years} åren` : `Area price trend, last ${years} years`;
};

const fill = (element, data, swedish) => {
    const win = priceChangeOverYears(data.series, DEFAULT_TREND_YEARS);
    if (!win) { element.style.display = 'none'; return; }
    const whole = win.change;
    const up = whole >= 0;
    element.innerHTML = `<span class="area-card-trend-arrow">${up ? '↗' : '↘'}</span>${up ? '+' : '−'}${Math.abs(whole)}${' '}%`;
    element.classList.toggle('is-down', !up);
    element.classList.add('is-filled');
    element.title = trendTitle(win, DEFAULT_TREND_YEARS, swedish);
};

const hydrateOne = (element) => {
    if (element.__priceTrendBound) return;
    element.__priceTrendBound = true;
    const regso = element.getAttribute('data-area-price-trend');
    if (!regso) return;
    const swedish = element.getAttribute('data-ph-sv') !== '0';
    setTimeout(() => {
        if (!element.isConnected) return;
        fetchAreaSeries(regso).then((data) => {
            if (!element.isConnected) return;
            if (!data) { element.style.display = 'none'; return; }
            fill(element, data, swedish);
        });
    }, HOVER_DELAY_MS);
};

// One observer watches for area cards being inserted anywhere and fills their trend chip; install-once.
let installed = false;
export const installAreaPriceTrendObserver = () => {
    if (installed || typeof document === 'undefined' || typeof MutationObserver === 'undefined') return;
    installed = true;
    const scan = (node) => {
        if (!node || node.nodeType !== 1) return;
        if (node.matches && node.matches('[data-area-price-trend]')) hydrateOne(node);
        if (node.querySelectorAll) node.querySelectorAll('[data-area-price-trend]').forEach(hydrateOne);
    };
    const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => mutation.addedNodes.forEach(scan));
    });
    observer.observe(document.body, { childList: true, subtree: true });
    scan(document.body); // any card already on screen
};

// The chip placeholder the card's heading embeds (hidden until hydrated with data).
// inline: in the line under the area's name (a card with a Dela button, design A of "Dela områden"), not a chip
// at the heading's end.
export const areaPriceTrendPlaceholder = (regso, swedish, inline = false) => (regso
    ? `<span class="area-card-trend${inline ? ' is-inline' : ''}" data-area-price-trend="${regso}" data-ph-sv="${swedish ? '1' : '0'}"></span>`
    : '');
